import { doc, updateDoc, getDoc, setDoc } from 'firebase/firestore';
import { ref, uploadBytes, deleteObject, getDownloadURL, getBytes } from 'firebase/storage';
import { httpsCallable } from 'firebase/functions';
import { db, storage, functions } from '../firebase';

/**
 * Upload icon to Storage and update Firestore manifest
 * @param {File} file - SVG file to upload
 * @param {string} iconName - Name of the icon (without .svg extension)
 * @returns {Promise<string>} Version hash
 */
export async function uploadIcon(file, iconName) {
  // 1. Generate hash for versioning
  const arrayBuffer = await file.arrayBuffer();
  const hashBuffer = await crypto.subtle.digest('SHA-256', arrayBuffer);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const hash = hashArray.map(b => b.toString(16).padStart(2, '0')).join('').slice(0, 8);
  const version = `v1_${hash}`;

  // 2. Upload to Storage
  const storageRef = ref(storage, `custom-icons/${iconName}.svg`);
  await uploadBytes(storageRef, file);

  // 3. Update Firestore manifest
  const manifestRef = doc(db, 'metadata', 'customIcons');

  // Check if document exists, if not create it
  const manifestDoc = await getDoc(manifestRef);
  if (!manifestDoc.exists()) {
    await setDoc(manifestRef, {
      manifest: {},
      lastUpdated: new Date()
    });
  }

  await updateDoc(manifestRef, {
    [`manifest.${iconName}`]: version,
    lastUpdated: new Date()
  });

  return version;
}

/**
 * Delete icon from Storage and remove from Firestore manifest
 * @param {string} iconName - Name of the icon to delete
 */
export async function deleteIcon(iconName) {
  // 1. Delete from Storage
  const storageRef = ref(storage, `custom-icons/${iconName}.svg`);
  await deleteObject(storageRef);

  // 2. Remove from Firestore manifest
  const manifestRef = doc(db, 'metadata', 'customIcons');
  const manifestDoc = await getDoc(manifestRef);
  const manifest = manifestDoc.data()?.manifest || {};
  delete manifest[iconName];

  await updateDoc(manifestRef, {
    manifest,
    lastUpdated: new Date()
  });
}

/**
 * Get current icon manifest from Firestore
 * @returns {Promise<Object>} Manifest object (iconName -> version)
 */
export async function getIconManifest() {
  const manifestRef = doc(db, 'metadata', 'customIcons');
  const manifestDoc = await getDoc(manifestRef);
  return manifestDoc.data()?.manifest || {};
}

/**
 * Get download URL for an icon
 * @param {string} iconName - Name of the icon
 * @returns {Promise<string>} Download URL
 */
export async function getIconUrl(iconName) {
  const storageRef = ref(storage, `custom-icons/${iconName}.svg`);
  return await getDownloadURL(storageRef);
}

const iconSvgs = new Map();

// The icon's SVG source, fetched once; null when it can't be read.
export function getIconSvg(iconName) {
  if (!iconSvgs.has(iconName)) {
    iconSvgs.set(iconName, getBytes(ref(storage, `custom-icons/${iconName}.svg`))
      .then(bytes => new TextDecoder().decode(bytes))
      .catch(() => null));
  }
  return iconSvgs.get(iconName);
}

// Width over height of the icon's viewBox, which custom icons crop tight to the
// drawing. Mirrors svgAspectRatio in the app; null when it can't be read.
export async function getIconAspectRatio(iconName) {
  const viewBox = (await getIconSvg(iconName))?.match(/viewBox\s*=\s*"([^"]+)"/);
  const values = viewBox ? viewBox[1].trim().split(/[\s,]+/).map(Number) : [];
  return values.length === 4 && values[3] > 0 ? values[2] / values[3] : null;
}

/**
 * Check which icons are used in puzzles
 * @param {Array} puzzles - Array of puzzle documents
 * @returns {Object} Icon usage count (iconName -> count)
 */
export function checkIconUsage(puzzles) {
  const iconPattern = /\{c\.([a-z0-9_]+)\}/g;
  const usage = {};

  puzzles.forEach(puzzle => {
    // Scan all clues in the puzzle
    (puzzle.words || []).forEach(word => {
      if (word.clue) {
        const matches = [...word.clue.matchAll(iconPattern)];
        matches.forEach(match => {
          const iconName = match[1];
          usage[iconName] = (usage[iconName] || 0) + 1;
        });
      }
    });
  });

  return usage;
}

/**
 * Extract required icons from a puzzle
 * @param {Object} puzzle - Puzzle object
 * @returns {Array<string>} Array of required icon names
 */
export function extractRequiredIcons(puzzle) {
  const iconPattern = /\{c\.([a-z0-9_]+)\}/g;
  const requiredIcons = new Set();

  // Scan all clues
  (puzzle.words || []).forEach(word => {
    if (word.clue) {
      const matches = [...word.clue.matchAll(iconPattern)];
      matches.forEach(match => {
        requiredIcons.add(match[1]);
      });
    }
  });

  return Array.from(requiredIcons);
}

/**
 * Validate icon name
 * @param {string} name - Icon name to validate
 * @returns {boolean} True if valid
 */
export function isValidIconName(name) {
  // Only lowercase alphanumeric and underscores
  return /^[a-z0-9_]+$/.test(name);
}

/**
 * Validate SVG file
 * @param {File} file - File to validate
 * @returns {Object} { valid: boolean, error?: string }
 */
export function validateSvgFile(file) {
  if (!file.name.toLowerCase().endsWith('.svg')) {
    return { valid: false, error: 'File must be an SVG (.svg)' };
  }

  const maxSize = 100 * 1024;
  if (file.size > maxSize) {
    return { valid: false, error: `File too large (${(file.size / 1024).toFixed(1)}KB). Maximum 100KB.` };
  }

  return { valid: true };
}

/**
 * Download all icons as a ZIP file for bundling in the app
 * @returns {Promise<Blob>} ZIP file blob
 */
export async function downloadAllIcons() {
  const downloadIcons = httpsCallable(functions, 'downloadAllIcons');
  const result = await downloadIcons();

  const base64Data = result.data.data;
  const binaryString = atob(base64Data);
  const bytes = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }

  return new Blob([bytes], { type: 'application/zip' });
}
